const chapter = {
  id: 'basic-concepts',
  slug: 'basic-concepts',
  subject: 'chemistry',
  subjectLabel: 'Chemistry',
  exam: 'JEE Main + NEET',
  title: {
    english: 'Some Basic Concepts of Chemistry',
        hindi: 'रसायन विज्ञान की कुछ मूल अवधारणाएँ',
  },
  estReadMinutes: 55,
  difficulty: {
    english: 'Easy',
        hindi: 'आसान',
  },
  weightage: {
    english: '1–2 questions in JEE Main / NEET',
        hindi: 'जेईई मेन/नीट में 1-2 प्रश्न',
  },
  syllabusTags: ['Physical Chemistry', 'JEE-Main', 'NEET', 'Class-11'],
  sections: [
    {
      id: 'laws',
      title: {
        english: 'Laws of Chemical Combination',
        hindi: 'रासायनिक संयोजन के नियम',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Law of Conservation of Mass** (Lavoisier): Mass is neither created nor destroyed in a chemical reaction.',
        hindi: '**द्रव्यमान के संरक्षण का नियम** (लैवोज़ियर): रासायनिक प्रतिक्रिया में द्रव्यमान न तो बनता है और न ही नष्ट होता है।',
            },
            {
              english: '**Law of Definite Proportions** (Proust): A given compound always contains the same elements in the same proportion by mass.',
        hindi: '**निश्चित अनुपात का नियम** (प्राउस्ट): किसी दिए गए यौगिक में हमेशा द्रव्यमान के अनुसार समान अनुपात में समान तत्व होते हैं।',
            },
            {
              english: '**Law of Multiple Proportions** (Dalton): When two elements form more than one compound, the masses of one element that combine with a fixed mass of the other are in a small whole-number ratio. (e.g. CO and CO₂: O ratio 1:2.)',
        hindi: '**एकाधिक अनुपात का नियम** (डाल्टन): जब दो तत्व एक से अधिक यौगिक बनाते हैं, तो एक तत्व का द्रव्यमान जो दूसरे तत्व के निश्चित द्रव्यमान के साथ जुड़ता है, एक छोटे पूर्णांक अनुपात में होता है। (उदाहरण के लिए CO और CO₂: O अनुपात 1:2.)',
            },
            {
              english: '**Gay-Lussac\'s Law of Gaseous Volumes**: Gases combine in volumes in simple whole-number ratios at the same $T, P$.',
        hindi: '**गे-लुसाक का गैसीय आयतन का नियम**: गैसें समान $T, P$ पर सरल पूर्ण-संख्या अनुपात में आयतन में संयोजित होती हैं।',
            },
            {
              english: '**Avogadro\'s Law**: Equal volumes of gases at the same $T, P$ contain equal numbers of molecules.',
        hindi: '**एवोगैड्रो का नियम**: समान $T, P$ पर गैसों की समान मात्रा में समान संख्या में अणु होते हैं।',
            },
          ],
        },
        {
          type: 'note',
          text: {
            english: '**Dalton\'s atomic theory** (1808) explained the first three laws: matter is made of indivisible atoms; atoms of the same element are identical; compounds form by combination of whole-number atoms.',
        hindi: '**डाल्टन के परमाणु सिद्धांत** (1808) ने पहले तीन नियमों की व्याख्या की: पदार्थ अविभाज्य परमाणुओं से बना है; एक ही तत्व के परमाणु समान होते हैं; यौगिक पूर्ण संख्या वाले परमाणुओं के संयोजन से बनते हैं।',
          },
        },
      ],
    },
    {
      id: 'atomic-mass',
      title: {
        english: 'Atomic & Molecular Masses, Mole Concept',
        hindi: 'परमाणु और आण्विक द्रव्यमान, मोल अवधारणा',
      },
      blocks: [
        {
          type: 'definition',
          term: {
            english: 'Atomic mass unit (amu / u)',
        hindi: 'परमाणु द्रव्यमान इकाई (एएमयू/यू)',
          },
          body: {
            english: '$1$ u = $1/12$ of mass of a $^{12}$C atom = $1.6605\\times 10^{-24}$ g. Atomic masses listed in tables are dimensionless ratios on this scale.',
        hindi: '$1$ u = of mass of a C परमाणु = g। तालिकाओं में सूचीबद्ध परमाणु द्रव्यमान इस पैमाने पर आयामहीन अनुपात हैं।',
          },
        },
        {
          type: 'theorem',
          name: {
            english: 'Mole concept',
        hindi: 'मोल अवधारणा',
          },
          statement: {
            english: `$1$ mole = $N_A = 6.022\\times 10^{23}$ entities. Mass of 1 mole of substance (in g) = its atomic / molecular mass (in u).
**Number of moles**: $n = \\dfrac{m}{M} = \\dfrac{N}{N_A} = \\dfrac{V_{\\text{gas, STP}}}{22.4\\ \\text{L}}$.`,
            hindi: '$1$ mole = entities। 1 मोल पदार्थ का द्रव्यमान (g में) = उसका परमाणु/आणविक द्रव्यमान (u में)। **मोल्स की संख्या**: $n = \\dfrac{m}{M} = \\dfrac{N}{N_A} = \\dfrac{V_{\\text{gas, STP}}}{22.4\\\\\\\\ \\text{L}}$.',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: 'Average atomic mass = $\\sum (x_i \\cdot m_i)$ where $x_i$ = fractional abundance of isotope $i$.',
        hindi: 'औसत परमाणु mass = where = fractional abundance of isotope । **अनुभवजन्य सूत्र**: परमाणुओं का सबसे सरल पूर्ण-संख्या अनुपात (उदाहरण के लिए बेंजीन के लिए CH)। **आण्विक सूत्र** = $\\sum (x_i \\cdot m_i)$ अनुभवजन्य सूत्र, जहां $x_i$।',
            },
            {
              english: '**Empirical formula**: simplest whole-number ratio of atoms (e.g. CH for benzene).',
        hindi: '**अनुभवजन्य सूत्र**: परमाणुओं का सबसे सरल पूर्ण-संख्या अनुपात (जैसे बेंजीन के लिए सीएच)।',
            },
            {
              english: '**Molecular formula** = $n \\times $ empirical formula, where $n = M_{\\text{mol}}/M_{\\text{emp}}$.',
        hindi: '**आण्विक सूत्र** = $n \\times $ अनुभवजन्य सूत्र, जहां $n = M_{\\text{mol}}/M_{\\text{emp}}$।',
            },
            {
              english: '**% composition by mass** of element $E$: $\\%E = (n_E \\cdot M_E /M_{\\text{compound}}) \\cdot 100$.',
        hindi: '**तत्व $E$ का द्रव्यमान अनुसार% संघटन**: $\\%E = (n_E \\cdot M_E /M_{\\text{compound}}) \\cdot 100$।',
            },
          ],
        },
        {
          type: 'example',
          number: 1,
          problem: {
            english: 'A compound has 40% C, 6.7% H, 53.3% O. Empirical formula? (At. masses: C=12, H=1, O=16.)',
        hindi: 'एक यौगिक में 40% C, 6.7% H, 53.3% O है। अनुभवजन्य सूत्र? (जनता पर: C=12, H=1, O=16।)',
          },
          solution: {
            english: `Moles: C = 40/12 = 3.33; H = 6.7/1 = 6.7; O = 53.3/16 = 3.33.
Divide by smallest (3.33): C : H : O = 1 : 2 : 1 ⇒ **CH₂O**.`,
            hindi: 'मोल्स: C = 40/12 = 3.33; H = 6.7/1 = 6.7; O = 53.3/16 = 3.33. सबसे छोटे से विभाजित करें (3.33): C : H : O = 1 : 2 : 1 ⇒ **CH₂O**। मोल्स: H₂ = O = 1 : 2 : 1 ⇒ **CH₂O** = 2; O₂ = 7/1 = 1. अनुपात C = 40/12 = 3, इसलिए **कोई भी अधिक नहीं** है। सभी H₂ और O₂ का उपभोग किया गया। H₂O के मोल H = 6. O = 53.',
          },
        },
      ],
    },
    {
      id: 'stoichiometry',
      title: {
        english: 'Stoichiometry & Limiting Reagent',
        hindi: 'रससमीकरणमिति और सीमाकारक अभिकर्मक',
      },
      blocks: [
        {
          type: 'paragraph',
          text: {
            english: 'A balanced chemical equation gives **mole ratios** of reactants and products. Convert masses → moles via molar mass, apply stoichiometric ratio, then convert back.',
        hindi: 'एक संतुलित रासायनिक समीकरण अभिकारकों और उत्पादों का **मोल अनुपात** देता है। द्रव्यमान → मोल को मोलर द्रव्यमान के माध्यम से परिवर्तित करें, स्टोइकोमेट्रिक अनुपात लागू करें, फिर वापस परिवर्तित करें।',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: '**Limiting reagent (LR)**: the reactant that is consumed first; it controls the amount of product. Identify by computing moles of each reactant ÷ its stoichiometric coefficient — smallest value is LR.',
        hindi: '**सीमित अभिकर्मक (एलआर)**: वह अभिकारक जिसका उपभोग सबसे पहले किया जाता है; यह उत्पाद की मात्रा को नियंत्रित करता है। प्रत्येक अभिकारक के मोल की गणना करके पहचानें ÷ इसका स्टोइकोमेट्रिक गुणांक - सबसे छोटा मान LR है।',
            },
            {
              english: '**Excess reagent**: any other reactant, partially left after reaction.',
        hindi: '**अतिरिक्त अभिकर्मक**: कोई अन्य अभिकारक, प्रतिक्रिया के बाद आंशिक रूप से बचा हुआ।',
            },
            {
              english: '**% yield** = (actual yield / theoretical yield) × 100.',
        hindi: '**% उपज** = (वास्तविक उपज/सैद्धांतिक उपज) × 100।',
            },
          ],
        },
        {
          type: 'example',
          number: 2,
          problem: {
            english: '$2\\text{H}_2 + \\text{O}_2 \\to 2\\text{H}_2\\text{O}$. Given 4 g H₂ and 32 g O₂, find limiting reagent and mass of water formed.',
        hindi: '$2\\text{H}_2 + \\text{O}_2 \\to 2\\text{H}_2\\text{O}$. 4 g H₂ और 32 g O₂ दिए जाने पर, सीमित अभिकर्मक और बने पानी का द्रव्यमान ज्ञात कीजिए।',
          },
          solution: {
            english: `Moles: H₂ = 4/2 = 2; O₂ = 32/32 = 1.
Ratio needed = H₂ : O₂ = 2 : 1 — exactly satisfied, so **neither is in excess**. All H₂ and O₂ consumed.
Moles of H₂O formed = 2. Mass = 2 × 18 = **36 g**.`,
            hindi: 'मोल्स: H₂ = 4/2 = 2; O₂ = 32/32 = 1. अनुपात needed = H₂ : O₂ = 2 : 1 — exactly satisfied, इसलिए **कोई भी अधिक नहीं** है। सभी H₂ और O₂ का उपभोग किया गया। H₂O के मोल formed = 2. Mass = 2 × 18 = **36 g**.',
          },
        },
      ],
    },
    {
      id: 'concentration',
      title: {
        english: 'Concentration of Solutions',
        hindi: 'विलयनों की सान्द्रता',
      },
      blocks: [
        {
          type: 'table',
          headers: ['Term', 'Definition', 'Formula'],
          rows: [
            ['**Mass %**', 'Mass of solute per 100 g solution', '$(w/W)\\cdot 100$'],
            ['**Volume %**', 'Volume of solute per 100 mL solution', '$(v/V)\\cdot 100$'],
            ['**Mass / Volume %**', 'Mass solute per 100 mL solution', '$(w/V)\\cdot 100$'],
            ['**ppm**', 'Parts per million (very dilute)', '$(w_{\\text{solute}}/w_{\\text{soln}})\\cdot 10^6$'],
            ['**Mole fraction $x_A$**', 'Moles A / total moles', '$x_A = n_A/(n_A + n_B)$, $\\sum x_i = 1$'],
            ['**Molarity $M$**', 'Moles solute per **litre solution**', '$M = n/V_{\\text{soln (L)}}$. Temp-dependent.'],
            ['**Molality $m$**', 'Moles solute per **kg solvent**', '$m = n/W_{\\text{solvent (kg)}}$. Temp-independent.'],
            ['**Normality $N$**', 'Gram equivalents per litre', '$N = n_{\\text{eq}}/V_{(L)} = M \\cdot n_f$'],
          ],
        },
        {
          type: 'list',
          items: [
            {
              english: '**Dilution**: $M_1 V_1 = M_2 V_2$ (moles conserved when adding solvent).',
        hindi: '** तनुकरण**: $M_1 V_1 = M_2 V_2$ (विलायक मिलाते समय मोल्स संरक्षित)।',
            },
            {
              english: '**Mixing**: $M = (M_1 V_1 + M_2 V_2 + \\ldots)/(V_1 + V_2 + \\ldots)$.',
        hindi: '**मिश्रण**: $M = (M_1 V_1 + M_2 V_2 + \\ldots)/(V_1 + V_2 + \\ldots)$.',
            },
            {
              english: '**Molarity ↔ Molality**: $m = \\dfrac{1000\\,M}{1000\\,d - M\\,M_{\\text{solute}}}$ where $d$ = density of solution in g/mL.',
        hindi: '**मोलैरिटी ↔ मोलैलिटी**: $m = \\dfrac{1000\\,M}{1000\\,d - M\\,M_{\\text{solute}}}$ जहां $d$ = g/mL में घोल का घनत्व।',
            },
            {
              english: '**Mole fraction ↔ Molality**: $m = \\dfrac{1000\\,x_A}{M_B (1 - x_A)}$ where B = solvent.',
        hindi: '**मोल अंश ↔ मोलैलिटी**: $m = \\dfrac{1000\\,x_A}{M_B (1 - x_A)}$ जहां B = विलायक।',
            },
          ],
        },
      ],
    },
    {
      id: 'sig-fig',
      title: {
        english: 'Significant Figures & Precision',
        hindi: 'सार्थक अंक और परिशुद्धता',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'All non-zero digits are significant.',
        hindi: 'सभी गैर-शून्य अंक महत्वपूर्ण हैं।',
            },
            {
              english: 'Zeros between non-zero digits are significant. Leading zeros are NOT.',
        hindi: 'गैर-शून्य अंकों के बीच शून्य महत्वपूर्ण हैं। अग्रणी शून्य नहीं हैं.',
            },
            {
              english: 'Trailing zeros in a decimal number are significant (e.g. 2.50 has 3 SF).',
        hindi: 'दशमलव संख्या में अनुगामी शून्य महत्वपूर्ण होते हैं (उदाहरण के लिए 2.50 में 3 SF होते हैं)।',
            },
            {
              english: 'In addition/subtraction: result has same **decimal places** as the term with fewest decimals.',
        hindi: 'addition/subtraction में: परिणाम में सबसे कम दशमलव वाले पद के समान **दशमलव स्थान** होते हैं।',
            },
            {
              english: 'In multiplication/division: result has same **significant figures** as the factor with fewest SF.',
        hindi: 'multiplication/division में: परिणाम में सबसे कम एसएफ वाले कारक के समान **महत्वपूर्ण आंकड़े** हैं।',
            },
          ],
        },
      ],
    },
  ],
  keyFormulas: [
    {
      latex: 'n = m/M = N/N_A',
      desc: {
        english: 'Number of moles',
        hindi: 'मोल्स की संख्या',
      },
    },
    {
      latex: 'V_{\\text{STP}} = 22.4\\ \\text{L/mol}',
      desc: {
        english: 'Molar volume (gas, STP)',
        hindi: 'मोलर आयतन (गैस, एसटीपी)',
      },
    },
    {
      latex: 'M = n/V_{(L)}',
      desc: {
        english: 'Molarity',
        hindi: 'मोलरिटी',
      },
    },
    {
      latex: 'm = n/W_{(\\text{kg solvent})}',
      desc: {
        english: 'Molality',
        hindi: 'मोलैलिटी',
      },
    },
    {
      latex: 'M_1 V_1 = M_2 V_2',
      desc: {
        english: 'Dilution',
        hindi: 'तनुकरण',
      },
    },
    {
      latex: 'x_A = n_A/(n_A + n_B)',
      desc: {
        english: 'Mole fraction',
        hindi: 'मोल अंश',
      },
    },
    {
      latex: '\\%E = (n_E M_E/M_{\\text{compound}})\\cdot 100',
      desc: {
        english: '% by mass',
        hindi: 'द्रव्यमान के अनुसार %',
      },
    },
  ],
  pyqs: [
    {
      year: 2024,
      exam: 'JEE Main',
      tag: 'Mole',
      problem: {
        english: 'How many molecules are in 4.4 g of CO₂? (M = 44)',
        hindi: '4.4 ग्राम CO₂ में कितने अणु होते हैं? (M = 44)',
      },
      answer: {
        english: '$6.022\\times 10^{22}$',
        hindi: '$6.022\\times 10^{22}$',
      },
      solution: {
        english: '$n = 4.4/44 = 0.1$ mol. $N = 0.1 \\cdot N_A$.',
        hindi: '$n = 4.4/44 = 0.1$ मोल। $N = 0.1 \\cdot N_A$.',
      },
    },
    {
      year: 2023,
      exam: 'NEET',
      tag: 'Empirical',
      problem: {
        english: 'A hydrocarbon contains 85.7% C and 14.3% H. Empirical formula?',
        hindi: 'एक हाइड्रोकार्बन में 85.7% C और 14.3% H होता है। अनुभवजन्य सूत्र?',
      },
      answer: {
        english: 'CH₂',
        hindi: 'CH₂',
      },
      solution: {
        english: 'C = 85.7/12 = 7.14; H = 14.3/1 = 14.3. Ratio 1:2.',
        hindi: 'सी = 85.7/12 = 7.14; एच = 14.3/1 = 14.3. अनुपात 1:2.',
      },
    },
    {
      year: 2022,
      exam: 'JEE Main',
      tag: 'Limiting',
      problem: {
        english: 'In $\\text{N}_2 + 3\\text{H}_2 \\to 2\\text{NH}_3$, 28 g N₂ + 9 g H₂. LR?',
        hindi: '$\\text{N}_2 + 3\\text{H}_2 \\to 2\\text{NH}_3$ में, 28 ग्राम N₂ + 9 ग्राम H₂। एलआर?',
      },
      answer: {
        english: 'H₂',
        hindi: 'H₂',
      },
      solution: {
        english: 'N₂ = 1 mol → needs 3 mol H₂. Available H₂ = 9/2 = 4.5 mol — H₂ would be in excess if 3 mol needed; recompute: ratio n/coeff: N₂ = 1/1 = 1, H₂ = 4.5/3 = 1.5. Smallest = N₂. So **N₂ is LR**.',
        hindi: 'N₂ = 1 mol → 3 mol H₂ की आवश्यकता है। उपलब्ध H₂ = 9/2 = 4.5 mol - यदि 3 mol की आवश्यकता हो तो H₂ अधिक मात्रा में होगा; पुनर्गणना: अनुपात n/coeff: N₂ = 1/1 = 1, H₂ = 4.5/3 = 1.5। Smallest = N₂. तो **N₂ LR** है।',
      },
    },
    {
      year: 2021,
      exam: 'NEET',
      tag: 'Concentration',
      problem: {
        english: '40 g NaOH dissolved in water to make 250 mL solution. Molarity? (M = 40)',
        hindi: '250 एमएल घोल बनाने के लिए 40 ग्राम NaOH को पानी में घोला गया। मोलरिटी? (M = 40)',
      },
      answer: {
        english: '$4$ M',
        hindi: '$4$ एम',
      },
      solution: {
        english: '$n = 1$ mol, $V = 0.25$ L, $M = 4$ M.',
        hindi: '$n = 1$ mol, $V = 0.25$ L, $M = 4$ M.',
      },
    },
    {
      year: 2020,
      exam: 'JEE Main',
      tag: 'Sig fig',
      problem: {
        english: 'How many significant figures in 0.005010?',
        hindi: '0.005010 में कितने सार्थक अंक हैं?',
      },
      answer: {
        english: '$4$',
        hindi: '$4$',
      },
      solution: {
        english: 'Leading zeros not counted; 5, 0, 1, 0 → 4 SF.',
        hindi: 'अग्रणी शून्यों की गणना नहीं की गई; 5, 0, 1, 0 → 4 एसएफ।',
      },
    },
  ],
  next: {
    slug: 'atomic-structure',
    title: 'Atomic Structure',
  },
};

export default chapter;
